import { useState } from "react";
import { motion } from "framer-motion";
import { Leaf, Recycle, Sprout, Factory } from "lucide-react";
import { IMAGES } from "../data";

const CREDS = [
  { icon: Leaf, title: "FSC® Certified", text: "Responsibly sourced paperboard from managed forests." },
  { icon: Recycle, title: "100% Recyclable", text: "EN 13430 certified — cups re-enter the paper stream." },
  { icon: Sprout, title: "Compostable Option", text: "EN 13432 plant-based lining breaks down in 12 weeks." },
  { icon: Factory, title: "Zero Waste Print", text: "Renewable-energy presses, 0% manufacturing waste." },
];

export const Sustainability = () => {
  const [mode, setMode] = useState("paper");

  return (
    <section id="eco" className="py-24 sm:py-32 bg-[#F3EFEA] overflow-hidden" data-testid="sustainability-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-14 items-center">
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="relative order-2 lg:order-1"
        >
          <div className="rounded-[2.5rem] overflow-hidden border-8 border-white shadow-2xl shadow-[#2E1065]/15 -rotate-2">
            <img
              src={IMAGES.baristaCounter}
              alt="Barista serving drinks at a local café counter"
              className="w-full h-[24rem] sm:h-[28rem] object-cover"
              data-testid="eco-image"
            />
          </div>

          <div className="absolute -bottom-6 -right-2 sm:right-6 bg-white rounded-3xl p-6 shadow-2xl w-72 rotate-1" data-testid="eco-impact-toggle-card">
            <div className="flex bg-[#F3EFEA] rounded-full p-1 mb-5" data-testid="eco-impact-toggle">
              {["paper", "plastic"].map((m) => (
                <button
                  key={m}
                  onClick={() => setMode(m)}
                  className={`flex-1 text-xs font-black uppercase tracking-widest rounded-full py-2 transition-all duration-300 ${
                    mode === m ? "bg-[#0D9488] text-white shadow" : "text-[#2E1065]/50"
                  }`}
                  data-testid={`eco-toggle-${m}`}
                >
                  {m}
                </button>
              ))}
            </div>
            {[
              { label: "Decomposition", paper: 92, plastic: 8 },
              { label: "Recyclability", paper: 96, plastic: 30 },
              { label: "Customer goodwill", paper: 88, plastic: 22 },
            ].map((row) => (
              <div key={row.label} className="mb-4 last:mb-0">
                <div className="flex justify-between text-xs font-bold text-[#2E1065]/70 mb-1.5">
                  <span>{row.label}</span>
                  <span data-testid={`eco-bar-value-${row.label.toLowerCase().replace(/ /g, "-")}`}>
                    {mode === "paper" ? row.paper : row.plastic}%
                  </span>
                </div>
                <div className="h-2.5 rounded-full bg-[#2E1065]/10 overflow-hidden">
                  <motion.div
                    className={`h-full rounded-full ${mode === "paper" ? "bg-[#0D9488]" : "bg-[#FF5A36]"}`}
                    animate={{ width: `${mode === "paper" ? row.paper : row.plastic}%` }}
                    transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                  />
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="order-1 lg:order-2"
        >
          <p className="text-xs sm:text-sm font-bold uppercase tracking-[0.25em] text-[#0D9488] mb-4" data-testid="eco-eyebrow">
            Circular & Responsible
          </p>
          <h2 className="font-display font-extrabold tracking-tight text-3xl sm:text-4xl lg:text-5xl text-[#2E1065] leading-tight" data-testid="eco-headline">
            Loud on branding.{" "}
            <span className="italic text-[#0D9488]">Light on the planet.</span>
          </h2>
          <p className="mt-5 text-[#2E1065]/70 leading-relaxed">
            Your customers care what their cup is made of — and they notice.
            Every Cup Connect order ships with certified recyclable or
            compostable stock, so your brand stands for something bigger.
          </p>
          <div className="mt-9 grid sm:grid-cols-2 gap-4" data-testid="eco-credentials-grid">
            {CREDS.map((c, i) => (
              <motion.div
                key={c.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="bg-white rounded-2xl p-5 border border-[#2E1065]/10 hover:border-[#0D9488]/40 hover:-translate-y-1 transition-all duration-300"
                data-testid={`eco-credential-${i}`}
              >
                <c.icon className="w-6 h-6 text-[#0D9488] mb-3" />
                <p className="font-display font-bold text-[#2E1065]">{c.title}</p>
                <p className="text-sm text-[#2E1065]/60 mt-1.5 leading-relaxed">{c.text}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};
